import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import AddressFields from '@/components/AddressFields'
import { Input, Spinner } from '@/components/ui'
import { createBien, updateBien, emptyBien } from './biensSlice'
import { typeBienOptions } from './mandat'
import SuggestionImmeuble from './info_bien/SuggestionImmeuble'

import useFormConfirmation from '@/hooks/useFormConfirmation.jsx'

const BienForm = ({ onSuccess = () => {}, onClose = () => {} }) => {
  const dispatch = useDispatch()
  const bien = useSelector((state) => state.biens.current || emptyBien)
  const errors = useSelector((state) => state.biens.requestStatus.errors)
  const isCreating = useSelector((state) => state.biens.loadingStatus.create)
  const isUpdating = useSelector((state) => state.biens.loadingStatus.update)
  const isLoading = isCreating || isUpdating

  const { handleSubmit, watch, setValue, getValues, control, reset, formState: { isDirty, errors: formErrors } } = useForm({
    defaultValues: bien,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const typeBien = watch('type_bien')
  const immeuble = watch('immeuble')

  // Synchroniser le formulaire lorsque bien change
  useEffect(() => {
    if (bien) {
      Object.entries(bien).forEach(([key, value]) => {
        setValue(key, value)
      })
    }
  }, [bien, setValue])

  useEffect(() => {
    if((typeBien === 'appartement' || typeBien === 'autre') && immeuble) {
      if(immeuble.id !== bien.immeuble_id){
        setValue('adresse_ligne1', immeuble.adresse_ligne1)
        setValue('adresse_ligne2', immeuble.adresse_ligne2)
        setValue('adresse_ligne3', immeuble.adresse_ligne3)
        setValue('code_postal', immeuble.code_postal)
        setValue('ville', immeuble.ville)
      }
    }
  }, [typeBien, immeuble])

  const onSubmit = async (formData) => {
    if (bien.id) {
      await dispatch(updateBien({ id: bien.id, bienData: formData }))
    } else {
      await dispatch(createBien(formData))
    }
    onSuccess?.()
  }

  const onReset = () => {
    reset(bien)
  }

  const {
    handleFormCancel,
    FormConfirmDialog
  } = useFormConfirmation({
    isDirty,
    onReset,
    onClose
  })

  // Fonction utilitaire pour obtenir l'erreur combinée (du form ou de l'API)
  const getErrorMessage = (fieldName) =>
    formErrors?.[fieldName]?.message || errors?.[fieldName] || ''

  // Fonction utilitaire pour déterminer si un champ a une erreur
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName] 

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Ajouter un bien</h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="card bg-base-100 border border-base-200 rounded-none mb-4">
          <div className="card-body p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col gap-4">
                </div>

                <div className="flex flex-col items-start gap-4">
                  <Input.Text control={control}
                    name="codage_interne"
                    label="Codage"
                    rules={{ required: "Le codage est requis" }}
                    validator={{ hasError, getErrorMessage }}
                  />
                  <Controller
                    name="type_bien"
                    control={control}
                    rules={{ required: "Le type de bien est requis" }}
                    render={({ field }) => (
                      <>
                        <label className="select validator w-2/3 md:w-full"
                              {...(hasError('type_bien') ? {'aria-invalid': true} : {})}
                        >
                          <span className="label">Type de bien</span>
                          <select
                            {...field}
                            value={field.value || ''}
                            disabled={bien?.type_bien}
                          >
                            <option value="" disabled>Sélectionnez un type</option>
                            {typeBienOptions.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <p className="validator-hint hidden w-2/3 mt-0 empty:py-[0.6rem]">{getErrorMessage('type_bien')}</p>
                      </>
                    )}
                  />

                  { typeBien === 'appartement' &&
                    <Controller
                      name="numero_appartement"
                      control={control}
                      render={({ field }) => (
                        <label className="input w-2:3 md:w-full">
                          <span className="label">N° appartement</span>
                          <input
                            type="text"
                            {...field}
                            value={field.value || ''}
                          />
                        </label>
                      )}
                    />
                   }
                  
                  { (typeBien === 'appartement' || typeBien === 'autre') &&
                    <Controller 
                      name="immeuble"
                      control={control}
                      defaultValue=""
                      render={({ field  }) => {
                        return (
                          <SuggestionImmeuble
                              objectField={field}
                              originalData={bien.immeuble}
                          />
                        );
                      }}
                    />
                   }
                </div>

                <div className="flex flex-col items-start gap-4">
                  <AddressFields control={control} without={['pays']} errors={errors} />
                </div>
            </div>
          </div>
        </div>
 
        <div className="flex justify-between">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => handleFormCancel()}
          >
            Annuler
          </button>
          <button
            type="submit"
            className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2 mt-4`}
            disabled={isLoading}
          >
            {isLoading ? <Spinner/> : 'Enregistrer'}
          </button>
        </div>
      </form>
      <FormConfirmDialog />
    </div>
  )
}

export default BienForm